import { useState } from "react";
import { toast } from "sonner";
import { LockKey, UserCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import CompanyForm from "@/components/CompanyForm";
import NumberingForm from "@/components/NumberingForm";
import UsersPanel from "@/components/UsersPanel";
import DangerZone from "@/components/DangerZone";
import AiModelCard from "@/components/AiModelCard";
import SessionsCard from "@/components/SessionsCard";
import InstallAppCard from "@/components/InstallAppCard";
import ThemeCard from "@/components/ThemeCard";
import { api, errMsg } from "@/lib/api";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

function PasswordForm() {
  const [f, setF] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.new_password.length < 8) return setErr("Yeni şifre en az 8 karakter olmalı");
    if (f.new_password !== f.confirm) return setErr("Yeni şifreler birbiriyle eşleşmiyor");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Şifreniz değiştirildi");
      setF(EMPTY);
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="panel fade-up space-y-4 p-6" data-testid="change-password-form">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold"><LockKey size={18} /> Şifre Değiştir</h3>
      <Field label="Mevcut Şifre"><Input type="password" required value={f.current_password} onChange={set("current_password")} data-testid="current-password-input" /></Field>
      <Field label="Yeni Şifre"><Input type="password" required value={f.new_password} onChange={set("new_password")} data-testid="new-password-input" /></Field>
      <Field label="Yeni Şifre (Tekrar)"><Input type="password" required value={f.confirm} onChange={set("confirm")} data-testid="confirm-password-input" /></Field>
      <p className="text-[11px] text-muted-foreground">En az 8 karakter kullanın.</p>
      {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="change-password-error">{err}</div>}
      <Button type="submit" disabled={busy} className="rounded-[4px]" data-testid="change-password-submit-button">{busy ? "Kaydediliyor…" : "Şifreyi Güncelle"}</Button>
    </form>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "admin";
  return (
    <div>
      <PageHeader eyebrow="Hesap" title="Ayarlar" />
      <div className="grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
        <div className="panel fade-up h-fit p-6" data-testid="profile-card">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold"><UserCircle size={18} /> Kullanıcı Bilgileri</h3>
          <div className="label-eyebrow">E-posta</div>
          <div className="mt-0.5 mb-3 text-sm" data-testid="profile-email">{user?.email}</div>
          <div className="label-eyebrow">Ad</div>
          <div className="mt-0.5 mb-3 text-sm">{user?.name}</div>
          <div className="label-eyebrow">Yetki</div>
          <div className="mt-0.5 text-sm">{{ owner: "İşveren", admin: "Yönetici", user: "Kullanıcı" }[user?.role] || user?.role}</div>
        </div>
        <PasswordForm />
      </div>
      <div className="mt-6 max-w-4xl">
        <CompanyForm canEdit={isManager} />
      </div>
      <div className="mt-6 max-w-4xl">
        <NumberingForm canEdit={isManager} />
      </div>
      {isManager && <div className="mt-6 max-w-4xl"><AiModelCard canEdit={user?.role === "owner"} /></div>}
      <div className="mt-6 max-w-4xl"><ThemeCard canEdit={isManager} /></div>
      <div className="mt-6 max-w-4xl"><SessionsCard /></div>
      <div className="mt-6 max-w-4xl"><InstallAppCard /></div>
      {isManager && <div className="mt-6 max-w-4xl"><UsersPanel /></div>}
      {user?.role === "owner" && <div className="mt-6 max-w-4xl"><DangerZone /></div>}
    </div>
  );
}
